import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import * as HeatmapOverlay from 'heatmap.js/plugins/leaflet-heatmap/leaflet-heatmap';
import { useQuery } from '@apollo/client';
import {
    FORECASTING_RESULTS_QUERY,
    FORECASTING_SCREEN_RESULTS_QUERY, ForecastingResultsQuery,
    ForecastingResultsQuery_forecastingResults_data_map,
    ForecastingResultsQueryVariables
} from 'data/ForecastingResults';
import NGMapContainer from 'leaflet/MapContainer';
import { FeatureGroup, Marker, Polygon, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { cellToBoundary, cellToLatLng } from 'h3-js';
import { DivIcon, LatLngBounds, LatLngTuple } from 'leaflet';
import { GeoQueryBounds, leafletBoundsToQueryVariables } from 'data/helpers';
import 'react-datepicker/dist/react-datepicker.css';
import SummaryStatistics from 'components/SummaryStatistics';
import ExportScreenList from 'components/ExportScreenList';
import './map.css';
import ForecastingPanel from 'components/forecasting-panel/ForecastingPanel';
import { useForecastingState, useVisualizationSettings, VisualizationType } from 'components/ForecastingContextProvider';
import {
    ForecastingScreenResultsQuery, ForecastingScreenResultsQuery_forecastingResults_data_screens,
    ForecastingScreenResultsQueryVariables
} from '../data/__generated__/ForecastingScreenResultsQuery';
import { colourToHtml, parseColour } from '../leaflet/util/ColourGradient';
import colourGradient from '../leaflet/util/ColourGradient';
import {formatNumber, displayKey} from '../leaflet/util/SummaryFormatting';

const colourLookup = colourGradient([
    {
        scale: 'log',
        startNumber: 1,
        endNumber: 100,
        colour: {
            start: parseColour('#ffbb0b')!,
            end: parseColour('#00aab4')!,
        },
    },
    {
        scale: 'log',
        startNumber: 100,
        endNumber: 1000,
        colour: {
            start: parseColour('#00aab4')!,
            end: parseColour('#0099fa')!,
        },
    },
    {
        scale: 'log',
        startNumber: 1000,
        endNumber: 10000,
        colour: {
            start: parseColour('#0099fa')!,
            end: parseColour('#004580')!,
        },
    },
    {
        scale: 'log',
        startNumber: 10000,
        endNumber: 1000000,
        colour: {
            start: parseColour('#004580')!,
            end: parseColour('#000000')!,
        },
    },
]);

function CircleLayer(
    {
        flyDest, 
        setFlyDest
    }: {
        flyDest: LatLngBounds | null, 
        setFlyDest: React.Dispatch<React.SetStateAction<LatLngBounds | null>>
    }) {
    const { visualizationType } = useVisualizationSettings();
    const { forecastingState, forecastingId } = useForecastingState();

    const map = useMap();
    const [bounds, setBounds] = useState(map.getBounds());
    const [zoomedAllIn, setZoomedAllIn] = useState(false);

    useMapEvents({
        zoom() {
            setBounds(map.getBounds());
            setZoomedAllIn(map.getMaxZoom() - map.getZoom() < 1.0);
        },
        moveend() {
            setBounds(map.getBounds());
            setFlyDest(null);
        },
    });

    const [queryBounds, setQueryBounds] = useState<GeoQueryBounds>(leafletBoundsToQueryVariables(bounds));

    const hexData = useQuery<
        ForecastingResultsQuery,
        ForecastingResultsQueryVariables
    >(FORECASTING_RESULTS_QUERY(visualizationType), {
        variables: {
            ...queryBounds,
            forecastingId: forecastingId!,
        },
        skip: zoomedAllIn || forecastingState !== 'loaded',
    });

    const pinData = useQuery<
        ForecastingScreenResultsQuery,
        ForecastingScreenResultsQueryVariables
    >(FORECASTING_SCREEN_RESULTS_QUERY, {
        variables: {
            ...queryBounds,
            forecastingId: forecastingId!,
        },
        skip: !zoomedAllIn || forecastingState !== 'loaded',
    });

    const loading = zoomedAllIn ? pinData.loading : hexData.loading;
    const latestData = useMemo<{
        type: 'pin',
        data: ForecastingScreenResultsQuery_forecastingResults_data_screens[],
    } | {
        type: 'hex',
        data: ForecastingResultsQuery_forecastingResults_data_map[],
    } | null>(() =>
        zoomedAllIn ? (pinData.data ? {
            type: 'pin',
            data: pinData.data.forecastingResults.data?.screens ?? [],
        } : null) : (hexData.data ? {
            type: 'hex',
            data: hexData.data.forecastingResults.data?.map ?? [],
        } : null),
        [zoomedAllIn ? pinData : hexData]);

    useEffect(() => {
        if (flyDest || !loading) {
            setQueryBounds(leafletBoundsToQueryVariables(flyDest ?? bounds))
        }
    }, [bounds, loading, flyDest]);

    const [dataOrPrevious, setDataOrPrevious] = useState(latestData);
    useEffect(() => {
        // creating new hexes on the screen while in 'fly' mode doesn't work, so we only update once it lands
        if (latestData && latestData !== dataOrPrevious && flyDest === null) setDataOrPrevious(latestData);
    }, [dataOrPrevious, latestData, flyDest]);
    useEffect(() => {
        setDataOrPrevious(latestData)
    }, [forecastingId, forecastingState]);

    const displayHexes = useMemo(() => {
        if (dataOrPrevious?.type === 'hex') {
            return dataOrPrevious.data.flatMap(({ h3, data }) => {
                const center = cellToLatLng(h3);
                const boundary = cellToBoundary(h3);
                if (center[1] < -160 || center[1] > 160) {
                    // Those that appear near the meridian get drawn on both sides.
                    // If the viewport is zoomed in to one side, then the other side just won't show, but it will be there.
                    const posVersion = {
                        center: center[1] > 0 ? center : [center[0], center[1] + 360] as [number, number],
                        boundary: boundary.map(it => it[1] > 0 ? it : [it[0], it[1] + 360]) as [number, number][],
                        data,
                        h3,
                        projection: 'pos',
                    };
                    const negVersion = {
                        center: center[1] < 0 ? center : [center[0], center[1] - 360] as [number, number],
                        boundary: boundary.map(it => it[1] < 0 ? it : [it[0], it[1] - 360]) as [number, number][],
                        data,
                        h3,
                        projection: 'neg',
                    };
                    return [posVersion, negVersion];
                }
                return [{
                    center,
                    boundary,
                    data,
                    h3,
                    projection: '',
                }];
            }).map(({ h3, data, boundary, center, projection }) => {
                const count: number = data[visualizationType as VisualizationType] ?? 0;
                const icon = new DivIcon({
                    html: `<p>${loading ? '...' : formatNumber(data, visualizationType as VisualizationType)}</p>`,
                });
                const onClick = () => {
                    const zoom = map.getBoundsZoom(boundary);
                    // @ts-ignore
                    const eventualPixelBounds = map.getPixelBounds(center, zoom);
                    setFlyDest(new LatLngBounds(
                        map.unproject(eventualPixelBounds.getBottomLeft(), zoom),
                        map.unproject(eventualPixelBounds.getTopRight(), zoom),
                    ));
                    map.flyTo(center, zoom, { easeLinearity: 1, animate: true });
                };
                const colour = ((it: number) => {
                    return colourToHtml(colourLookup(it));
                })(count);
                return <div key={`${h3}-div-${projection}`}>
                    <Marker
                        key={`${h3}-label-${projection}`}
                        position={center}
                        icon={icon}
                        eventHandlers={{ click: onClick }}
                    />
                    <Polygon
                        key={`${h3}-${colour}-${projection}`}
                        positions={boundary}
                        fillColor={colour}
                        color={colour}
                        weight={1}
                        opacity={1}
                        fillOpacity={0.6}
                        eventHandlers={{ click: onClick }}
                    />
                </div>;
            });
        } else if (dataOrPrevious?.type === 'pin') {
            return Object.values(dataOrPrevious.data
                .flatMap(item => {
                    if (item.longitude < -160 || item.longitude > 160) {
                        // Those that appear near the meridian get drawn on both sides.
                        // If the viewport is zoomed in to one side, then the other side just won't show, but it will be there.
                        const posVersion = {
                            ...item,
                            longitude: item.longitude < 0 ? item.longitude + 360 : item.longitude,
                        };
                        const negVersion = {
                            ...item,
                            longitude: item.longitude < 0 ? item.longitude : item.longitude - 360,
                        };
                        return [posVersion, negVersion];
                    }
                    return [item];
                })
                .reduce<{[key: string]: ForecastingScreenResultsQuery_forecastingResults_data_screens[]}>((acc, next) => {
                    const key = `${next.latitude}-${next.longitude}`;
                    acc[key] = acc[key] ? [...acc[key], next] : [next];
                    return acc;
                }, {}))
                .map(items => {
                    const onClick = async () => { await navigator.clipboard.writeText(items.map(it => it.screenId).join('\n')); };
                    return <div key={`${items[0].longitude}-${items[0].latitude}-div`}>
                        <Marker
                            key={`${items[0].longitude}-${items[0].latitude}-marker`}
                            position={[items[0].latitude, items[0].longitude]}
                            eventHandlers={{ click: onClick }}
                        >
                            <Tooltip
                                key={`${items[0].longitude}-${items[0].latitude}-tooltip`}
                                eventHandlers={{ click: onClick }}
                            >
                                <table>
                                    <tr>
                                        <th className={'px-1'}>Screen ID</th>
                                        <th className={'px-1'}>Publisher</th>
                                        <th className={'px-1'}>Venue type</th>
                                        {visualizationType === 'numberOfScreens' ? <></> : <th className={'px-1'}>{displayKey(visualizationType as VisualizationType)}</th>}
                                    </tr>
                                    {items.sort((a, b) => a.screenId.localeCompare(b.screenId)).map(it =>
                                        <tr key={it.screenId}>
                                            <td className={'px-1'}>{it.screenId}</td>
                                            <td className={'px-1'}>{it.publisher ? it.publisher : <em>unavailable</em>}</td>
                                            <td className={'px-1'}>{it.venueType ? it.venueType : <em>unavailable</em>}</td>
                                            {visualizationType === 'numberOfScreens' ? <></> :<td className={'px-1'}>{formatNumber(it, visualizationType as VisualizationType)}</td>}
                                        </tr>
                                    )}
                                </table>
                            </Tooltip>
                        </Marker>
                    </div>;
                });
        } else {
            return <></>;
        }

    }, [dataOrPrevious, visualizationType]);

    return (
        <>
            <FeatureGroup key={'featureGroup'}>
                {displayHexes}
            </FeatureGroup>
        </>
    );
}

export default function SampledAvails() {
    const startingPoint: LatLngTuple = [53.68, -2.62];
    const controlsRef = useRef();
    const [flyDest, setFlyDest] = useState<LatLngBounds | null>(null);



    return (
        <div className='relative h-full flex-grow flex'>
            <div className='leaflet-top leaflet-left'>
                <ForecastingPanel />
            </div>
            <NGMapContainer
                center={startingPoint}
                defaultZoom={6}
                controlsRef={controlsRef}
            >
                <CircleLayer flyDest={flyDest} setFlyDest={setFlyDest}/>
                <SummaryStatistics setFlyDest={setFlyDest}/>
            </NGMapContainer>
            <ExportScreenList />
        </div>
    );
}
